import Reveal from '../components/Reveal';
import PosterColumn from '../components/PosterColumn';
import { useLanguage } from '../i18n/LanguageContext';
import { homeContent } from '../i18n/content/home';

import predatorBadlands from '../assets/series/predator-badlands.webp';
import thursdayMurderClub from '../assets/series/thursday-murder-club.webp';
import conjuringLastRites from '../assets/series/conjuring-last-rites.webp';
import mortalKombat2 from '../assets/series/mortal-kombat-2.webp';
import theOdyssey from '../assets/series/the-odyssey.webp';
import manhunt from '../assets/series/manhunt.webp';

import championsLeague2526 from '../assets/sports/champions-league-2526.webp';
import elClasico from '../assets/sports/el-clasico.webp';
import wakeUpDeadMan from '../assets/series/wake-up-dead-man.webp';
import avatarFireAndAsh from '../assets/series/avatar-fire-and-ash.webp';
import laligaDazn from '../assets/sports/laliga-dazn.webp';
import euro2024Champions from '../assets/sports/euro-2024-champions.webp';

import swiped from '../assets/series/swiped.webp';
import trasElVerano from '../assets/series/tras-el-verano.webp';
import anaconda from '../assets/series/anaconda.webp';
import zootopia2 from '../assets/series/zootopia-2.webp';
import bundesligaStars from '../assets/sports/bundesliga-stars.webp';
import serieA2526 from '../assets/sports/serie-a-2526.webp';

// Poster showcase config — each column is independently tunable (direction,
// loop speed, stagger delay and card width) without touching the markup.
const CAROUSEL_COLUMNS = [
    {
        key: 'col-1',
        direction: 'up',
        duration: 32,
        delay: -4,
        weight: 1,
        className: 'poster-column--sm mt-0',
        posters: [
            { src: predatorBadlands, alt: 'Predator: Badlands' },
            { src: thursdayMurderClub, alt: 'The Thursday Murder Club' },
            { src: conjuringLastRites, alt: 'The Conjuring: Last Rites' },
            { src: mortalKombat2, alt: 'Mortal Kombat II' },
            { src: theOdyssey, alt: 'The Odyssey' },
            { src: manhunt, alt: 'Manhunt' },
        ],
    },
    {
        key: 'col-2',
        direction: 'down',
        duration: 38,
        delay: -16,
        weight: 1.3,
        className: 'poster-column--lg -translate-y-6 md:-translate-y-10',
        posters: [
            { src: championsLeague2526, alt: 'Champions League 2025/26', badge: 'LIVE' },
            { src: elClasico, alt: 'El Clásico' },
            { src: wakeUpDeadMan, alt: 'Wake Up Dead Man' },
            { src: avatarFireAndAsh, alt: 'Avatar: Fire and Ash' },
            { src: laligaDazn, alt: 'LaLiga' },
            { src: euro2024Champions, alt: 'Euro 2024 Campeones' },
        ],
    },
    {
        key: 'col-3',
        direction: 'up',
        duration: 25,
        delay: -9,
        weight: 0.9,
        className: 'poster-column--xs mt-4',
        posters: [
            { src: swiped, alt: 'Swiped' },
            { src: trasElVerano, alt: 'Tras el Verano' },
            { src: anaconda, alt: 'Anaconda' },
            { src: zootopia2, alt: 'Zootopia 2' },
            { src: bundesligaStars, alt: 'Bundesliga' },
            { src: serieA2526, alt: 'Serie A' },
        ],
    },
];

export default function Home() {
    const { lang } = useLanguage();
    const c = homeContent[lang];
    return (
        <section className="home-section relative overflow-hidden" id="home">
            <div className="absolute inset-0 z-0">
                <div className="absolute inset-0 bg-gradient-to-b from-primary-light/60 via-white to-white"></div>
                <div className="absolute inset-0 hero-dot-grid"></div>
                <div className="absolute inset-0 ambient-glow"></div>
            </div>

            <div className="main-content relative z-10 py-6 sm:py-16 lg:py-0">
                <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-8 items-center min-w-0">
                    <Reveal className="flex flex-col gap-6 order-1 min-w-0">
                        <span className="hero-badge">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"><path fill="currentColor" d="M7 2v11h3v9l7-12h-4l3-8z"></path></svg>
                            {c.badge}
                        </span>

                        <h1 className="font-extrabold text-ink leading-[1.03] tracking-tight" style={{ fontSize: 'clamp(2.5rem, 1.7rem + 3.4vw, 4.5rem)' }}>
                            {c.titleLine1}
                            <br className="hidden sm:block" /> {c.titleLine2}<span className="hero-gradient-text">{c.titleGradient}</span>
                        </h1>

                        <p className="text-base md:text-lg text-ink-muted leading-relaxed max-w-lg">
                            {c.subtitle}
                        </p>

                        <div className='flex flex-wrap items-center gap-4 mt-1'>
                            <a href="#services" className='btn-glow btn-glow-gold gold-gradient text-ink px-9 py-3.5 rounded-full font-bold text-lg transition-all duration-300 hover:scale-[1.03] shadow-lg'>
                                <span>{c.ctaOffers}</span>
                            </a>
                            <a href="#sports" className='px-9 py-3.5 rounded-full font-semibold text-base border border-ink/15 bg-white text-ink transition-all duration-300 hover:border-primary hover:text-primary'>
                                {c.ctaSport}
                            </a>
                        </div>

                        {/* Activation badge */}
                        <div className="flex flex-wrap items-center gap-4 mb-8">
                            <span className="flex items-center gap-2 bg-white border border-line rounded-full px-4 py-1.5 shadow-sm">
                                <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse"></span>
                                <p className='text-ink text-xs font-semibold'>{c.activationBadge}</p>
                            </span>
                        </div>
                    </Reveal>

                    {/* 3-column cinematic poster showcase */}
                    <Reveal delay={150} className="relative order-2 hero-showcase">
                        <div className="absolute inset-0 hero-showcase-glow" aria-hidden="true"></div>

                        <div className="relative flex justify-center items-stretch gap-3 sm:gap-4 h-[380px] sm:h-[460px] lg:h-[560px]">
                            {CAROUSEL_COLUMNS.map((col) => (
                                <PosterColumn
                                    key={col.key}
                                    posters={col.posters}
                                    direction={col.direction}
                                    duration={col.duration}
                                    delay={col.delay}
                                    weight={col.weight}
                                    className={col.className}
                                />
                            ))}
                        </div>

                        {/* Floating quality badge */}
                        <div className="hidden sm:flex absolute -top-3 left-1/2 -translate-x-1/2 lg:left-0 lg:translate-x-0 items-center gap-2 bg-ink text-white rounded-full shadow-lg px-4 py-2 text-xs font-bold tracking-wide">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"><path fill="#FBBF24" d="M7 2v11h3v9l7-12h-4l3-8z"></path></svg>
                            {c.qualityBadge}
                        </div>

                        {/* Floating live status card */}
                        <div className="hidden sm:flex absolute -bottom-4 -right-2 lg:right-4 bg-white/90 backdrop-blur-md rounded-card-md shadow-lg px-4 py-3 items-center gap-3 border border-line">
                            <span className="relative flex h-2.5 w-2.5">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
                            </span>
                            <div className="leading-tight">
                                <p className="text-sm font-bold text-ink">{c.liveNow}</p>
                                <p className="text-xs text-ink-muted">{c.liveChannels}</p>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    )
}
